import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Telescope, BrainCircuit, Heart, Trophy, Sparkles, Lightbulb, Rocket, Cog, HandHeart, ScanFace } from "lucide-react";
import { Overline, Reveal } from "../components/Section";
import { ASSETS } from "../lib/assets";

const pillars = [
  { l: "P", w: "Progress" },
  { l: "J", w: "Journey" },
  { l: "S", w: "Strength" },
  { l: "R", w: "Rewards" },
  { l: "S", w: "Sustainability" },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-slate-950 text-white">
      {/* HERO */}
      <section className="relative pt-40 pb-24 overflow-hidden grain">
        <div className="absolute inset-0">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-96 w-[600px] rounded-full bg-teal-500/15 blur-[120px]" />
          <div className="absolute inset-0 grid-bg opacity-30" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <Reveal><Overline>About PJSRS Infotech Private Limited</Overline></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.98]">
              An AI-first company<br />
              <span className="text-gradient-teal">built on five ideas.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl mx-auto text-lg text-slate-300 leading-relaxed">
              PJSRS Infotech Private Limited builds innovative digital products that inspire healthier
              lifestyles, empower individuals through talent recognition, and create engaging entertainment
              experiences — leveraging AI, automation, cloud, and data-driven innovation.
            </p>
          </Reveal>
        </div>
      </section>

      {/* VISION + MISSION */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full rounded-3xl border border-white/10 bg-white/[0.02] p-10 backdrop-blur-sm">
              <div className="h-12 w-12 rounded-xl bg-teal-400/10 border border-teal-400/20 grid place-items-center text-teal-300 mb-6">
                <Telescope className="h-5 w-5" />
              </div>
              <Overline>Our vision</Overline>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-black tracking-tighter leading-tight">
                A globally recognised AI-powered digital products company.
              </h2>
              <p className="mt-5 text-slate-400 leading-relaxed">
                To inspire healthier lives, empower talent, and create meaningful entertainment experiences
                for millions of people across the world.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-white/10 bg-white/[0.02] p-10 backdrop-blur-sm">
              <div className="h-12 w-12 rounded-xl bg-teal-400/10 border border-teal-400/20 grid place-items-center text-teal-300 mb-6">
                <Target className="h-5 w-5" />
              </div>
              <Overline>Our mission</Overline>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-black tracking-tighter leading-tight">
                Innovative, scalable, and intelligent platforms.
              </h2>
              <p className="mt-5 text-slate-400 leading-relaxed">
                To build digital products that combine Artificial Intelligence, human creativity, and
                technology to improve everyday life — at scale.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PJSRS Meaning */}
      <section className="relative py-24 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <Overline>The PJSRS Meaning</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter">
              Five letters. <span className="text-gradient-teal">A philosophy.</span>
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              These values guide every product, innovation, and experience we create.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-5 gap-px bg-white/5 border border-white/10 rounded-3xl overflow-hidden">
            {pillars.map((p, i) => (
              <Reveal key={p.w + i} delay={i * 0.05}>
                <div className="bg-slate-950 p-8 h-full hover:bg-slate-900/70 transition-colors">
                  <div className="font-display font-black text-6xl text-teal-400/90 leading-none">{p.l}</div>
                  <div className="mt-6 font-display text-xl font-bold text-white">{p.w}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STAND FOR */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Overline>What we stand for</Overline>
          <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter max-w-2xl">
            Inspire. <span className="text-gradient-teal">Empower.</span> Entertain.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Heart, t: "Inspire", desc: "Encourage healthy habits and positive lifestyle changes. Motivate users to achieve personal growth and well-being." },
              { icon: Trophy, t: "Empower", desc: "Recognize and promote emerging talent. Create opportunities for individuals to learn, grow, and succeed." },
              { icon: Sparkles, t: "Entertain", desc: "Deliver engaging, interactive, and rewarding digital experiences. Build communities around sports, storytelling, and gaming." },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-8 backdrop-blur-sm">
                  <div className="h-12 w-12 rounded-xl bg-teal-400/10 border border-teal-400/20 grid place-items-center text-teal-300 mb-6">
                    <c.icon className="h-5 w-5" />
                  </div>
                  <div className="font-display text-2xl font-bold text-white">{c.t}</div>
                  <p className="mt-3 text-slate-400 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI-FIRST COMPANY */}
      <section className="relative py-28 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 grid-bg opacity-20" />
          <div className="absolute -top-32 right-1/4 h-96 w-[500px] rounded-full bg-teal-500/15 blur-[120px]" />
          <div className="absolute -bottom-32 left-1/4 h-96 w-[500px] rounded-full bg-cyan-500/10 blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          {/* Intro */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 rounded-full bg-teal-400/10 border border-teal-400/30 px-4 py-2 text-xs text-teal-300 font-mono uppercase tracking-widest mb-6">
                <BrainCircuit className="h-3.5 w-3.5" /> AI-First Company
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[1.05]">
                AI is the engine<br />
                <span className="text-gradient-teal">behind everything we build.</span>
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-lg text-slate-300 leading-relaxed">
                PJSRS leverages Artificial Intelligence across product development, operations, analytics,
                and customer engagement to build innovative platforms that inspire healthy lifestyles,
                empower talent, and create engaging entertainment experiences.
              </p>

              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { i: Lightbulb, t: "Product Development" },
                  { i: Cog, t: "Operations" },
                  { i: BrainCircuit, t: "Analytics" },
                  { i: Sparkles, t: "Customer Engagement" },
                ].map((c, i) => (
                  <Reveal key={c.t} delay={i * 0.05}>
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:bg-white/[0.04] transition">
                      <c.i className="h-5 w-5 text-teal-300" />
                      <div className="mt-3 text-xs font-mono uppercase tracking-wider text-slate-300">{c.t}</div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>

          {/* AI Vision */}
          <Reveal>
            <div className="mt-16 rounded-3xl border border-teal-400/20 bg-gradient-to-br from-teal-950/40 via-slate-900 to-slate-950 p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <div className="h-14 w-14 rounded-2xl bg-teal-400/10 border border-teal-400/30 grid place-items-center text-teal-300 mb-6">
                  <Rocket className="h-6 w-6" />
                </div>
                <Overline>Our AI Vision</Overline>
                <h3 className="mt-4 font-display text-3xl md:text-4xl font-black tracking-tighter leading-[1.05]">
                  Maximise AI across <span className="text-gradient-teal">every stage</span> of the business lifecycle.
                </h3>
              </div>
              <div className="lg:col-span-7 flex flex-col justify-center gap-6 text-slate-300 leading-relaxed">
                <p>
                  We aim to maximize the use of Artificial Intelligence across every stage of the business
                  lifecycle — from idea generation to product delivery and day-to-day operations.
                </p>
                <p>
                  By combining human creativity with AI-driven intelligence, PJSRS seeks to build scalable,
                  innovative, and impactful digital products that inspire healthier lifestyles, empower
                  talent, and create engaging entertainment experiences.
                </p>
                <div className="pt-2">
                  <Link
                    to="/contact"
                    data-testid="about-cta-contact"
                    className="inline-flex items-center gap-2 rounded-full bg-teal-400 text-slate-950 px-6 py-3 text-sm font-bold hover:bg-teal-300 transition-all"
                  >
                    Partner with us <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Closing call-out */}
          <Reveal delay={0.15}>
            <div className="mt-12 text-center">
              <div className="font-display text-3xl md:text-5xl font-black tracking-tighter">
                Inspire. <span className="text-gradient-teal">Empower.</span> Entertain.
              </div>
              <div className="mt-4 font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-teal-300">
                — Powered by Artificial Intelligence —
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OUR COMMITMENT */}
      <section className="relative py-28 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 grid-bg opacity-15" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[800px] rounded-full bg-teal-500/20 blur-[140px]" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-teal-400/15 border border-teal-400/40 px-4 py-2 text-xs text-teal-300 font-mono uppercase tracking-[0.25em] mb-8">
                <Heart className="h-3.5 w-3.5" /> Our Commitment
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[1.05]">
                Every PJSRS product is guided<br />
                by <span className="text-gradient-teal">two questions.</span>
              </h2>
            </div>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <Reveal delay={0.1}>
              <div className="relative h-full rounded-3xl border border-teal-400/30 bg-gradient-to-br from-teal-950/60 via-slate-900 to-slate-950 p-10 md:p-12">
                <div className="absolute -top-8 -left-2 font-display text-[140px] leading-none font-black text-teal-400/15 select-none">01</div>
                <div className="relative">
                  <div className="h-14 w-14 rounded-2xl bg-teal-400/15 border border-teal-400/40 grid place-items-center text-teal-300 mb-8">
                    <ScanFace className="h-6 w-6" />
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-teal-400 mb-4">Question One</div>
                  <p className="font-display text-2xl md:text-3xl font-bold tracking-tight leading-[1.2] text-white">
                    How can <span className="text-gradient-teal">Artificial Intelligence</span> make this
                    product smarter, more effective, and more accessible?
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="relative h-full rounded-3xl border border-teal-400/30 bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950/60 p-10 md:p-12">
                <div className="absolute -top-8 -right-2 font-display text-[140px] leading-none font-black text-teal-400/15 select-none">02</div>
                <div className="relative">
                  <div className="h-14 w-14 rounded-2xl bg-teal-400/15 border border-teal-400/40 grid place-items-center text-teal-300 mb-8">
                    <HandHeart className="h-6 w-6" />
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-teal-400 mb-4">Question Two</div>
                  <p className="font-display text-2xl md:text-3xl font-bold tracking-tight leading-[1.2] text-white">
                    How can this product <span className="text-gradient-teal">positively impact</span>{" "}
                    people&apos;s lives and contribute to their personal growth, well-being, or happiness?
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.3}>
            <p className="mt-14 max-w-3xl mx-auto text-center text-lg text-slate-300 leading-relaxed">
              These principles define <span className="text-white font-semibold">who we are</span>,{" "}
              <span className="text-white font-semibold">what we build</span>, and the{" "}
              <span className="text-white font-semibold">future we strive to create</span>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* LOGO band */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center gap-10 justify-between">
          <div className="flex items-center gap-6">
            <div className="h-24 w-24 rounded-2xl bg-white grid place-items-center">
              <img src={ASSETS.logos.pjsrs} alt="PJSRS" className="h-20 w-20 object-contain" />
            </div>
            <div>
              <div className="font-display text-2xl font-black">PJSRS Infotech Private Limited</div>
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-teal-400 mt-1">Inspire · Empower · Entertain</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/products" data-testid="about-link-products" className="rounded-full bg-white/5 border border-white/10 px-5 py-3 text-sm hover:bg-white/10">Our products</Link>
            <Link to="/contact" data-testid="about-link-contact" className="rounded-full bg-teal-400 text-slate-950 px-5 py-3 text-sm font-bold hover:bg-teal-300">Contact</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
